import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Edit2, Copy, TrendingUp, TrendingDown, ArrowRightLeft, ChevronDown, ChevronRight, ArrowUp, ArrowDown } from "lucide-react";
import { useFinanceStore } from "../stores/finance-store";
import { CATEGORY_TYPES, isExpenseForReports, isIncomeForReports, isTransferTransaction } from "../lib/transaction-types";
import { filterByStatus, isTransactionInMonth } from "../lib/finance-calculations";
import {
  CATEGORY_CODE_MAX_DEPTH,
  compareCategoryCodes,
  flattenCategoryTree,
  generateNextCategoryCode,
  getCategoryCodeSegment,
  getDescendantIds,
  reassignCategorySubtreeCodes,
  validateCategoryParent,
} from "../lib/category-tree";
import { getTransactionCategory } from "../lib/category-relations";
import { formatCategoryName, formatCurrency, normalizeCategoryName, parseCurrency, stringToColor } from "../lib/utils";
import { ColorPicker, DEFAULT_CATEGORY_COLOR } from "../components/ColorPicker";
import { DataTable } from "../components/DataTable";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../components/ui/select";
import { toast } from "sonner";

export default function CategoriesPage() {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [activeTab, setActiveTab] = useState(CATEGORY_TYPES.EXPENSE);
  const [categoryName, setCategoryName] = useState("");
  const [parentId, setParentId] = useState("null");
  const [categoryColor, setCategoryColor] = useState(DEFAULT_CATEGORY_COLOR);
  const [expandedParents, setExpandedParents] = useState(() => new Set());
  const [sortConfig, setSortConfig] = useState({ field: "codigo", direction: "asc" });
  const [pendingParentUpdate, setPendingParentUpdate] = useState(null);
  const [cloneTarget, setCloneTarget] = useState(null);
  const [isCloning, setIsCloning] = useState(false);

  const {
    transactions,
    categories,
    fetchTransactions,
    fetchCategories,
    addCategory,
    updateCategory,
    deleteCategory,
    cloneCategory,
    isLoading,
  } = useFinanceStore();

  useEffect(() => {
    fetchTransactions();
    fetchCategories();
  }, [fetchTransactions, fetchCategories]);

  const categoryById = useMemo(() => (
    new Map(categories.map((category) => [category.id, category]))
  ), [categories]);

  const getCategoryColor = (category, visited = new Set()) => {
    if (!category) return DEFAULT_CATEGORY_COLOR;
    if (visited.has(category.id)) return category.color || DEFAULT_CATEGORY_COLOR;
    visited.add(category.id);

    if (category.parent_id) {
      const parent = categoryById.get(category.parent_id);
      const parentColor = parent ? getCategoryColor(parent, visited) : null;
      return parentColor || category.color || stringToColor(formatCategoryName(category.name));
    }

    return category.color || stringToColor(formatCategoryName(category.name));
  };

  const getParentCategory = (id) => {
    if (!id || id === "null") return null;
    return categoryById.get(id) || null;
  };

  const incomeCategories = useMemo(() => categories.filter(c => c.type === CATEGORY_TYPES.INCOME), [categories]);
  const expenseCategories = useMemo(() => categories.filter(c => c.type === CATEGORY_TYPES.EXPENSE), [categories]);
  const transferCategories = useMemo(() => categories.filter(c => c.type === CATEGORY_TYPES.TRANSFER), [categories]);
  const categoriesByType = useMemo(() => ({
    [CATEGORY_TYPES.EXPENSE]: expenseCategories,
    [CATEGORY_TYPES.INCOME]: incomeCategories,
    [CATEGORY_TYPES.TRANSFER]: transferCategories,
  }), [expenseCategories, incomeCategories, transferCategories]);

  const getCategoriesByType = (type) => {
    return categoriesByType[type] || expenseCategories;
  };

  const selectedParent = getParentCategory(parentId);
  const effectiveCategoryColor = selectedParent ? getCategoryColor(selectedParent) : categoryColor;
  const editCategory = editId ? categoryById.get(editId) : null;
  const descendantIds = useMemo(() => (
    editId ? getDescendantIds(categories, editId) : []
  ), [categories, editId]);

  const previewCode = useMemo(() => {
    const normalizedParentId = selectedParent?.id || null;
    if (editCategory && (editCategory.parent_id || null) === normalizedParentId) {
      return editCategory.codigo || generateNextCategoryCode(categories, activeTab, normalizedParentId, [editCategory.id]);
    }

    return generateNextCategoryCode(categories, activeTab, normalizedParentId, editCategory ? [editCategory.id, ...descendantIds] : []);
  }, [activeTab, categories, descendantIds, editCategory, selectedParent]);

  const parentOptions = useMemo(() => {
    const items = categoriesByType[activeTab] || expenseCategories;
    const rows = flattenCategoryTree(items, { includeCollapsedChildren: true });

    return rows.filter((category) => {
      if (category.id === editId) return false;
      if (descendantIds.includes(category.id)) return false;
      return validateCategoryParent(categories, editId, category.id, activeTab).valid;
    });
  }, [activeTab, categories, categoriesByType, descendantIds, editId, expenseCategories]);

  const handleOpenEdit = (category) => {
    setEditId(category.id);
    setActiveTab(category.type || CATEGORY_TYPES.EXPENSE);
    setCategoryName(normalizeCategoryName(category.name));
    setParentId(category.parent_id || "null");
    setCategoryColor(getCategoryColor(category));
    setShowAddDialog(true);
  };

  const handleParentChange = (value) => {
    setParentId(value);
    const parent = getParentCategory(value);
    if (parent) {
      setCategoryColor(getCategoryColor(parent));
    }
  };

  const openCreateDialog = () => {
    setEditId(null);
    setCategoryName("");
    setParentId("null");
    setCategoryColor(DEFAULT_CATEGORY_COLOR);
    setShowAddDialog(true);
  };

  const openCreateChildDialog = (parent) => {
    if (!parent || (parent._depth || 0) >= CATEGORY_CODE_MAX_DEPTH - 1) {
      toast.error(`A hierarquia permite no máximo ${CATEGORY_CODE_MAX_DEPTH} níveis.`);
      return;
    }

    setEditId(null);
    setActiveTab(parent.type || activeTab);
    setCategoryName("");
    setParentId(parent.id);
    setCategoryColor(getCategoryColor(parent));
    setShowAddDialog(true);
  };

  const resetDialog = () => {
    setCategoryName("");
    setParentId("null");
    setCategoryColor(DEFAULT_CATEGORY_COLOR);
    setEditId(null);
    setPendingParentUpdate(null);
    setShowAddDialog(false);
  };

  const buildCategoryPayload = () => {
    const normalizedName = normalizeCategoryName(categoryName);
    const parent = getParentCategory(parentId);
    const nextParentId = parent?.id || null;
    const validation = validateCategoryParent(categories, editId, nextParentId, activeTab);

    if (!normalizedName) {
      toast.error("Nome da categoria é obrigatório");
      return null;
    }

    if (!validation.valid) {
      toast.error(validation.message);
      return null;
    }

    const duplicatedName = getCategoriesByType(activeTab).some((category) => (
      category.id !== editId &&
      (category.parent_id || null) === nextParentId &&
      normalizeCategoryName(category.name) === normalizedName
    ));
    if (duplicatedName) {
      toast.error("Já existe uma categoria com esse nome neste pai");
      return null;
    }

    return {
      name: normalizedName,
      color: parent ? getCategoryColor(parent) : categoryColor || stringToColor(normalizedName),
      parent_id: nextParentId,
      codigo: previewCode,
    };
  };

  const updateDescendants = async ({ categoryId, color, codeUpdates }) => {
    const ids = getDescendantIds(categories, categoryId);
    for (const childId of ids) {
      const child = categoryById.get(childId);
      if (!child) continue;
      const result = await updateCategory(child.id, {
        name: normalizeCategoryName(child.name),
        color,
        parent_id: child.parent_id || null,
        codigo: codeUpdates.get(child.id) || child.codigo,
      });
      if (result?.success === false) return false;
    }
    return true;
  };

  const saveCategory = async (payload, options = {}) => {
    const updateCodes = Boolean(options.updateCodes);
    let result;

    if (editId) {
      const codeUpdates = updateCodes
        ? reassignCategorySubtreeCodes(categories, editId, payload.parent_id)
        : new Map();
      const editedPayload = {
        ...payload,
        codigo: codeUpdates.get(editId) || payload.codigo || editCategory?.codigo,
      };

      result = await updateCategory(editId, editedPayload);
      if (result.success) {
        const descendantsUpdated = await updateDescendants({
          categoryId: editId,
          color: editedPayload.color,
          codeUpdates,
        });
        if (!descendantsUpdated) {
          toast.error("Categoria atualizada, mas houve erro ao atualizar descendentes.");
          return;
        }
      }
    } else {
      if (!payload.codigo) {
        toast.error(`A hierarquia permite no máximo ${CATEGORY_CODE_MAX_DEPTH} níveis.`);
        return;
      }
      result = await addCategory(activeTab, payload);
    }

    if (result.success) {
      toast.success(editId ? "Categoria atualizada!" : "Categoria adicionada!");
      resetDialog();
    } else {
      toast.error(editId ? "Erro ao atualizar categoria" : "Erro ao adicionar categoria");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = buildCategoryPayload();
    if (!payload) return;

    if (editId && editCategory && (editCategory.parent_id || null) !== payload.parent_id) {
      setPendingParentUpdate(payload);
      return;
    }

    await saveCategory(payload, { updateCodes: false });
  };

  const handleParentCodeDecision = async (updateCodes) => {
    const payload = pendingParentUpdate;
    setPendingParentUpdate(null);
    if (!payload) return;
    await saveCategory(payload, { updateCodes });
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    if (getDescendantIds(categories, deleteId).length > 0) {
      toast.error("Mova ou exclua as categorias filhas antes de excluir esta categoria.");
      setDeleteId(null);
      return;
    }

    const result = await deleteCategory(deleteId);
    if (result.success) {
      toast.success("Categoria excluída!");
    } else {
      toast.error("Erro ao excluir categoria");
    }
    setDeleteId(null);
  };

  const runClone = async (category) => {
    if (!category || isCloning) return;

    setIsCloning(true);
    const result = await cloneCategory(category.id);
    setIsCloning(false);
    setCloneTarget(null);

    if (result?.success) {
      toast.success(`Categoria clonada (${result.created} ${result.created === 1 ? "item" : "itens"})`);
      return;
    }

    if (result?.rolledBack) {
      toast.error(`Erro ao clonar categoria. Clonagem revertida (0 de ${result.planned} itens criados).`);
      return;
    }

    if (result?.planned) {
      toast.error(`Erro ao clonar categoria. ${result.created} de ${result.planned} itens permaneceram; revise a lista.`);
      return;
    }

    toast.error(result?.error || "Erro ao clonar categoria");
  };

  const handleClone = (category) => {
    if (getDescendantIds(categories, category.id).length > 0) {
      setCloneTarget(category);
      return;
    }
    runClone(category);
  };

  const toggleParent = (categoryId) => {
    setExpandedParents((current) => {
      const next = new Set(current);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const handleSort = (field) => {
    setSortConfig((current) => {
      if (current.field !== field) {
        return { field, direction: field === "value" ? "desc" : "asc" };
      }

      return {
        field,
        direction: current.direction === "asc" ? "desc" : "asc",
      };
    });
  };

  const handleMobileSortChange = (value) => {
    const [field, direction] = value.split("-");
    setSortConfig({ field, direction });
  };

  const sortCategoryRows = (rows) => [...rows].sort((a, b) => {
    if (sortConfig.field === "value") {
      const diff = (Number(a._value) || 0) - (Number(b._value) || 0);
      return sortConfig.direction === "asc" ? diff : -diff;
    }

    if (sortConfig.field === "name") {
      const diff = formatCategoryName(a.name).localeCompare(formatCategoryName(b.name), "pt-BR");
      return sortConfig.direction === "asc" ? diff : -diff;
    }

    const diff = compareCategoryCodes(a, b);
    return sortConfig.direction === "asc" ? diff : -diff;
  });

  const getSortLabel = () => {
    if (sortConfig.field === "value") {
      return sortConfig.direction === "asc" ? "Valor menor primeiro" : "Valor maior primeiro";
    }
    if (sortConfig.field === "name") {
      return sortConfig.direction === "asc" ? "Nome A-Z" : "Nome Z-A";
    }
    return sortConfig.direction === "asc" ? "Código crescente" : "Código decrescente";
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <span className="inline-block h-3.5 w-3.5" />;
    }

    return sortConfig.direction === "asc"
      ? <ArrowUp className="h-3.5 w-3.5" />
      : <ArrowDown className="h-3.5 w-3.5" />;
  };

  const SortHeader = ({ field, children, align = "left" }) => (
    <button
      type="button"
      className={`inline-flex w-full items-center gap-1 text-sm font-medium ${align === "right" ? "justify-end" : "justify-start"}`}
      onClick={() => handleSort(field)}
    >
      <span>{children}</span>
      <SortIcon field={field} />
    </button>
  );

  const currentMonthCategoryValues = useMemo(() => {
    const today = new Date();
    const month = today.getMonth();
    const year = today.getFullYear();
    const valuesByType = {
      [CATEGORY_TYPES.EXPENSE]: new Map(),
      [CATEGORY_TYPES.INCOME]: new Map(),
      [CATEGORY_TYPES.TRANSFER]: new Map(),
    };

    const addValue = (type, tx, amount) => {
      const category = getTransactionCategory(tx, categories);
      const normalized = normalizeCategoryName(category?.name || tx.category);
      if (!normalized) return;
      const key = category?.id || normalized;
      const current = valuesByType[type].get(key) || 0;
      valuesByType[type].set(key, current + amount);
    };

    filterByStatus(transactions, true)
      .filter((tx) => isTransactionInMonth(tx, month, year, true))
      .forEach((tx) => {
        const amount = parseCurrency(tx.amount);
        if (isExpenseForReports(tx)) {
          addValue(CATEGORY_TYPES.EXPENSE, tx, amount);
        } else if (isIncomeForReports(tx)) {
          addValue(CATEGORY_TYPES.INCOME, tx, amount);
        }
      });

    const handledTransfers = new Set();
    filterByStatus(transactions, true)
      .filter((tx) => isTransactionInMonth(tx, month, year, true))
      .filter(isTransferTransaction)
      .forEach((tx) => {
        const transferKey = tx.linked_transfer_id || tx.id;
        if (handledTransfers.has(transferKey)) return;
        handledTransfers.add(transferKey);
        addValue(CATEGORY_TYPES.TRANSFER, tx, parseCurrency(tx.amount));
      });

    return valuesByType;
  }, [transactions, categories]);

  const formatPercent = (value) => new Intl.NumberFormat("pt-BR", {
    style: "percent",
    minimumFractionDigits: value > 0 && value < 0.1 ? 1 : 0,
    maximumFractionDigits: value > 0 && value < 0.1 ? 1 : 0,
  }).format(value || 0);

  const buildCategoryRows = (items, type) => {
    const validIds = new Set(items.map((category) => category.id));
    const rawValues = currentMonthCategoryValues[type] || new Map();
    const directValueById = new Map();
    const childrenByParent = new Map();

    items.forEach((category) => {
      directValueById.set(category.id, rawValues.get(category.id) || rawValues.get(normalizeCategoryName(category.name)) || 0);
      const parentKey = category.parent_id && validIds.has(category.parent_id) ? category.parent_id : "root";
      if (!childrenByParent.has(parentKey)) childrenByParent.set(parentKey, []);
      childrenByParent.get(parentKey).push(category);
    });

    const aggregateValue = (category, visited = new Set()) => {
      if (visited.has(category.id)) return 0;
      visited.add(category.id);
      const direct = directValueById.get(category.id) || 0;
      return (childrenByParent.get(category.id) || []).reduce(
        (sum, child) => sum + aggregateValue(child, new Set(visited)),
        direct
      );
    };

    const valueById = new Map();
    items.forEach((category) => valueById.set(category.id, aggregateValue(category)));
    const roots = childrenByParent.get("root") || [];
    const total = roots.reduce((sum, category) => sum + (valueById.get(category.id) || 0), 0);
    const flatData = [];

    const visit = (parentKey, depth, parentName = null, visited = new Set()) => {
      const rows = (childrenByParent.get(parentKey) || []).map((category) => ({
        ...category,
        _depth: depth,
        _parentName: parentName,
        _value: valueById.get(category.id) || 0,
        _percent: total > 0 ? (valueById.get(category.id) || 0) / total : 0,
        _childrenCount: (childrenByParent.get(category.id) || []).length,
        _isExpanded: expandedParents.has(category.id),
      }));

      sortCategoryRows(rows).forEach((row) => {
        if (visited.has(row.id)) return;
        flatData.push(row);
        if (row._isExpanded) {
          visit(row.id, depth + 1, formatCategoryName(row.name), new Set([...visited, row.id]));
        }
      });
    };

    visit("root", 0);
    return flatData;
  };

  const CategoryName = ({ row, compact = false }) => (
    <div
      className="flex min-w-0 items-center gap-2"
      style={{ paddingLeft: `${row._depth * (compact ? 14 : 18)}px` }}
      title={row.codigo ? `${row.codigo} - ${formatCategoryName(row.name)}` : formatCategoryName(row.name)}
    >
      {row._childrenCount > 0 ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={(event) => {
            event.stopPropagation();
            toggleParent(row.id);
          }}
          aria-label={row._isExpanded ? "Recolher categoria" : "Expandir categoria"}
        >
          {row._isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </Button>
      ) : (
        <span className="h-7 w-7 shrink-0" />
      )}
      <span
        className="h-3 w-3 shrink-0 rounded-full"
        style={{ backgroundColor: getCategoryColor(row) }}
      />
      <span className="shrink-0 rounded border bg-muted px-1.5 py-0.5 font-mono text-[11px] font-semibold text-muted-foreground">
        {getCategoryCodeSegment(row)}
      </span>
      <span className={row.parent_id ? "truncate text-muted-foreground" : "truncate font-medium text-primary"}>
        {formatCategoryName(row.name)}
      </span>
    </div>
  );

  const CategoryList = ({ items, emptyMessage, type }) => {
    const flatData = buildCategoryRows(items, type);

    return (
      <>
        <div className="hidden sm:block">
          <DataTable
            columns={[
              {
                key: "name",
                label: <SortHeader field="codigo">Categoria</SortHeader>,
                render: (row) => <CategoryName row={row} />
              },
              {
                key: "value",
                label: <SortHeader field="value" align="right">Valor</SortHeader>,
                align: "right",
                render: (row) => (
                  <span className="font-mono font-semibold">
                    {formatCurrency(row._value || 0)}
                  </span>
                )
              },
              {
                key: "percent",
                label: "%",
                align: "right",
                render: (row) => (
                  <span className="font-mono text-sm text-muted-foreground">
                    {formatPercent(row._percent)}
                  </span>
                )
              },
              {
                key: "actions",
                label: "Ações",
                align: "center",
                className: "w-40",
                render: (row) => (
                  <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-primary"
                      onClick={() => openCreateChildDialog(row)}
                      disabled={(row._depth || 0) >= CATEGORY_CODE_MAX_DEPTH - 1}
                      aria-label={`Adicionar filho de ${formatCategoryName(row.name)}`}
                      title={`Adicionar filho de ${formatCategoryName(row.name)}`}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => handleOpenEdit(row)}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-primary"
                      onClick={() => handleClone(row)}
                      disabled={isCloning}
                      aria-label={`Clonar ${formatCategoryName(row.name)}`}
                      title={`Clonar ${formatCategoryName(row.name)}`}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => setDeleteId(row.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )
              }
            ]}
            data={flatData}
            onRowClick={(row) => handleOpenEdit(row)}
            emptyState={
              <div className="empty-state py-8">
                <p className="text-muted-foreground mb-4">{emptyMessage}</p>
              </div>
            }
          />
        </div>

        <div className="space-y-2 sm:hidden">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-muted-foreground">Ordenar</span>
            <Select value={`${sortConfig.field}-${sortConfig.direction}`} onValueChange={handleMobileSortChange}>
              <SelectTrigger className="h-9 w-[205px]">
                <SelectValue placeholder={getSortLabel()} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="codigo-asc">Código crescente</SelectItem>
                <SelectItem value="codigo-desc">Código decrescente</SelectItem>
                <SelectItem value="name-asc">Nome A-Z</SelectItem>
                <SelectItem value="name-desc">Nome Z-A</SelectItem>
                <SelectItem value="value-desc">Valor maior primeiro</SelectItem>
                <SelectItem value="value-asc">Valor menor primeiro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {flatData.length > 0 ? flatData.map((row) => (
            <div
              key={row.id}
              role="button"
              tabIndex={0}
              className="rounded-lg border bg-card px-3 py-2.5 shadow-sm transition-colors hover:bg-muted/40"
              onClick={() => handleOpenEdit(row)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleOpenEdit(row);
                }
              }}
            >
              <div className="flex items-center justify-between gap-3">
                <CategoryName row={row} compact />
                <div className="flex shrink-0 items-center gap-1">
                  <p className="font-mono text-xs font-semibold text-muted-foreground">{formatPercent(row._percent)}</p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-primary"
                    onClick={(event) => {
                      event.stopPropagation();
                      openCreateChildDialog(row);
                    }}
                    disabled={(row._depth || 0) >= CATEGORY_CODE_MAX_DEPTH - 1}
                    aria-label={`Adicionar filho de ${formatCategoryName(row.name)}`}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-primary"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleClone(row);
                    }}
                    disabled={isCloning}
                    aria-label={`Clonar ${formatCategoryName(row.name)}`}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )) : (
            <div className="empty-state rounded-lg border bg-card py-8">
              <p className="text-muted-foreground">{emptyMessage}</p>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="space-y-6 pb-32 sm:pb-20 lg:pb-0">
      <div className="flex flex-wrap items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Categorias</h1>
          <p className="text-muted-foreground mt-1">
            Organize suas transações por categoria
          </p>
        </div>

        <Button onClick={openCreateDialog} className="btn-glow hidden sm:inline-flex" data-testid="add-category-btn">
          <Plus className="w-4 h-4 mr-2" />
          <span>Nova Categoria</span>
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 sm:w-auto">
          <TabsTrigger value={CATEGORY_TYPES.EXPENSE} className="flex items-center gap-2" data-testid="tab-expense">
            <TrendingDown className="w-4 h-4" />
            Despesas ({expenseCategories.length})
          </TabsTrigger>
          <TabsTrigger value={CATEGORY_TYPES.INCOME} className="flex items-center gap-2" data-testid="tab-income">
            <TrendingUp className="w-4 h-4" />
            Receitas ({incomeCategories.length})
          </TabsTrigger>
          <TabsTrigger value={CATEGORY_TYPES.TRANSFER} className="flex items-center gap-2" data-testid="tab-transfer">
            <ArrowRightLeft className="w-4 h-4" />
            Transferências ({transferCategories.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={CATEGORY_TYPES.EXPENSE} className="mt-6">
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16" />
              ))}
            </div>
          ) : (
            <CategoryList
              items={expenseCategories}
              type={CATEGORY_TYPES.EXPENSE}
              emptyMessage="Nenhuma categoria de despesa cadastrada"
            />
          )}
        </TabsContent>

        <TabsContent value={CATEGORY_TYPES.INCOME} className="mt-6">
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <CategoryList
              items={incomeCategories}
              type={CATEGORY_TYPES.INCOME}
              emptyMessage="Nenhuma categoria de receita cadastrada"
            />
          )}
        </TabsContent>

        <TabsContent value={CATEGORY_TYPES.TRANSFER} className="mt-6">
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <CategoryList
              items={transferCategories}
              type={CATEGORY_TYPES.TRANSFER}
              emptyMessage="Nenhuma categoria de transferência cadastrada"
            />
          )}
        </TabsContent>
      </Tabs>

      <Button
        onClick={openCreateDialog}
        className="btn-glow fixed bottom-20 left-1/2 z-40 h-12 w-[220px] -translate-x-1/2 rounded-full px-5 shadow-lg sm:hidden"
        data-testid="add-category-btn-mobile"
      >
        <Plus className="w-4 h-4 mr-2" />
        Nova Categoria
      </Button>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editId ? "Editar Categoria" : "Nova Categoria"}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Tabs value={activeTab} onValueChange={(value) => {
              setActiveTab(value);
              if (!editId) {
                setParentId("null");
                setCategoryColor(DEFAULT_CATEGORY_COLOR);
              }
            }}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value={CATEGORY_TYPES.EXPENSE} disabled={!!editId}>Despesa</TabsTrigger>
                <TabsTrigger value={CATEGORY_TYPES.INCOME} disabled={!!editId}>Receita</TabsTrigger>
                <TabsTrigger value={CATEGORY_TYPES.TRANSFER} disabled={!!editId}>Transferência</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="parentId">Categoria Pai (Opcional)</Label>
                <Select value={parentId} onValueChange={handleParentChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Nenhuma (Categoria Principal)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="null">Nenhuma (Categoria Principal)</SelectItem>
                    {parentOptions.map(parent => (
                      <SelectItem key={parent.id} value={parent.id}>
                        <span style={{ paddingLeft: `${parent._depth * 12}px` }}>
                          {getCategoryCodeSegment(parent)} - {formatCategoryName(parent.name)}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="rounded-md border bg-muted/40 px-3 py-2">
                <Label className="text-xs text-muted-foreground">Código completo</Label>
                <p className="mt-1 font-mono text-sm font-semibold">{previewCode || "Sem código disponível"}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="categoryName">Nome da Categoria</Label>
                <div className="flex items-center gap-3">
                  <div
                    className="w-6 h-6 rounded-full flex-shrink-0"
                    style={{ backgroundColor: effectiveCategoryColor || stringToColor(normalizeCategoryName(categoryName) || "DEFAULT") }}
                  />
                  <Input
                    id="categoryName"
                    placeholder={activeTab === CATEGORY_TYPES.TRANSFER ? "Ex: Reserva, Investimentos..." : "Ex: Alimentação, Salário..."}
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value.toLocaleUpperCase("pt-BR"))}
                    autoComplete="off"
                    data-testid="category-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Cor</Label>
                <ColorPicker
                  value={effectiveCategoryColor}
                  onChange={setCategoryColor}
                  disabled={!!selectedParent}
                />
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={resetDialog}
              >
                Cancelar
              </Button>
              {editId && (
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => {
                    setShowAddDialog(false);
                    setDeleteId(editId);
                  }}
                >
                  Excluir
                </Button>
              )}
              <Button type="submit" className="flex-1 btn-glow" data-testid="category-submit">
                {editId ? "Atualizar" : "Adicionar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!pendingParentUpdate} onOpenChange={(open) => !open && setPendingParentUpdate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Atualizar código da hierarquia?</AlertDialogTitle>
            <AlertDialogDescription>
              A categoria foi movida para outro pai. Atualize os códigos para herdar a nova posição ou mantenha os códigos atuais.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => handleParentCodeDecision(false)}>
              Manter código
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => handleParentCodeDecision(true)}>
              Atualizar códigos
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!cloneTarget} onOpenChange={() => !isCloning && setCloneTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clonar categoria?</AlertDialogTitle>
            <AlertDialogDescription>
              {cloneTarget
                ? `"${formatCategoryName(cloneTarget.name)}" e ${getDescendantIds(categories, cloneTarget.id).length} subcategoria(s) serão duplicadas com novos códigos. As transações não são afetadas.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCloning}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => runClone(cloneTarget)} disabled={isCloning}>
              {isCloning ? "Clonando..." : "Clonar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir categoria?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. As transações associadas a esta categoria não serão afetadas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
